import React from "react";
import Link from "gatsby-link";
import { rgba, shade } from "polished";
import styled, { ThemeProvider } from "styled-components";
import { Box, Flex } from "rebass";
import Text from "./Text";
import H4 from "./Headings/H4";
import H3 from "./Headings/H3";
import Button from "./Buttons/Button";
import OverlayImage from "./OverlayImage";
import theme from "../theme.js";
import invertTheme from "../invertTheme.js";
import { ArrowRight } from "react-feather";

const Action = styled(Box)`
  position: relative;
  display: block;
  text-decoration: none;
  opacity: 0;
  animation: tiny-tile-fade-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards;
  will-change: opacity;
  transition: transform 500ms cubic-bezier(0.23, 1, 0.32, 1),
    box-shadow 500ms cubic-bezier(0.23, 1, 0.32, 1);

  @keyframes tiny-tile-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  * {
    text-decoration: none;

    &::selection {
      color: ${(props) =>
        props.theme.colors[props.cardColor] &&
        shade(0.9375, props.theme.colors[props.cardColor])};
      background-color: ${(props) => props.theme.colors.grey[0]};
    }
  }

  &:not(:last-child) {
    > * {
      margin-bottom: 4px;
    }
  }
  &:hover {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
    transform: translateY(-2px);
    .tiny-tile__corner-icon {
      opacity: 0.5;
      transform: translateY(0);
    }
  }
`;

Action.defaultProps = {
  is: Link,
  to: "/",
};

const Wrapper = styled(Box)`
  position: relative;
  background-color: ${(props) => props.theme.colors[props.cardColor]};
  &:not(:lang(ar)) {
    &:before,
    &:after {
      right: 0;
    }
    &:after {
      background: ${(props) =>
        props.theme.colors[props.cardColor] &&
        "linear-gradient(to right, " +
          rgba(props.theme.colors[props.cardColor], 1) +
          ", " +
          rgba(props.theme.colors[props.cardColor], 0.75) +
          ")"};
    }
  }
  &:lang(ar) {
    &:before,
    &:after {
      left: 0;
    }
    &:after {
      background: ${(props) =>
        props.theme.colors[props.cardColor] &&
        "linear-gradient(to left, " +
          rgba(props.theme.colors[props.cardColor], 1) +
          ", " +
          rgba(props.theme.colors[props.cardColor], 0.75) +
          ")"};
    }
  }

  &:before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 0;
    width: 100%;
    max-width: 672px;
    height: 100%;
    filter: grayscale(1);
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;

    /* Fallback */
    background-image: ${(props) =>
      "url(" + (props.backgroundImage ? props.backgroundImage[2] : "") + ")"};

    /* Webkit fallback */
    background-image: ${(props) =>
      "-webkit-image-set(url(" +
      (props.backgroundImage ? props.backgroundImage[2] : "") +
      ") 1x, url(" +
      (props.backgroundImage ? props.backgroundImage[1] : "") +
      ") 2x)"};

    /* Regular imageset */
    background-image: ${(props) =>
      "image-set(url(" +
      (props.backgroundImage ? props.backgroundImage[2] : "") +
      ") 1x, url(" +
      (props.backgroundImage ? props.backgroundImage[1] : "") +
      ") 2x)"};

    @media (min-width: 52em) {
      /* Fallback */
      background-image: ${(props) =>
        "url(" + (props.backgroundImage ? props.backgroundImage[1] : "") + ")"};

      /* Webkit fallback */
      background-image: ${(props) =>
        "-webkit-image-set(url(" +
        (props.backgroundImage ? props.backgroundImage[1] : "") +
        ") 1x, url(" +
        (props.backgroundImage ? props.backgroundImage[1] : "") +
        ") 2x, url(" +
        (props.backgroundImage ? props.backgroundImage[0] : "") +
        ") 3x)"};

      /* Regular imageset */
      background-image: ${(props) =>
        "image-set(url(" +
        (props.backgroundImage ? props.backgroundImage[1] : "") +
        ") 1x, url(" +
        (props.backgroundImage ? props.backgroundImage[1] : "") +
        ") 2x, url(" +
        (props.backgroundImage ? props.backgroundImage[0] : "") +
        ") 3x)"};
    }

    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
      background-image: ${(props) =>
        props.theme.colors[props.cardColor] &&
        "linear-gradient(to bottom, " +
          (props.cardColor
            ? rgba(props.theme.colors[props.cardColor], 0.66)
            : "") +
          ", " +
          (props.cardColor
            ? rgba(props.theme.colors[props.cardColor], 0.66)
            : "") +
          "), url(" +
          (props.backgroundImage ? props.backgroundImage[2] : "") +
          ")"};
    }
  }

  &:after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 0;
    width: 100%;
    max-width: 672px;
    height: 100%;
  }
`;

const Content = styled(Box)`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 640px;
`;

const CornerIcon = styled(Box)`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
  width: 24px;
  height: 24px;
  background-color: ${(props) => props.theme.colors.grey[0]};
  border-radius: 9999px;
  opacity: 0;
  transform: translateY(4px);
  transition: transform 500ms cubic-bezier(0.23, 1, 0.32, 1),
    opacity 500ms cubic-bezier(0.23, 1, 0.32, 1);

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    stroke: ${(props) => props.theme.colors[props.cardColor]};
  }
`;

const determineColor = function (region) {
  let color = "violet";
  if (
    region === "middle-east-and-north-africa" ||
    region === "oriente-medio-y-norte-de-africa" ||
    region === "الشرق-الاوسط-وشمات-افريقيا"
  ) {
    color = "orange";
  }
  if (region === "africa" || region === "africa1" || region === "افريقيا") {
    color = "green";
  }
  if (
    region === "latin-america" ||
    region === "america-latina-y-el-caribe" ||
    region === "امريكا-اللاتينية"
  ) {
    color = "blue";
  }
  if (region === "asia" || region === "asia1" || region === "اسيا") {
    color = "pink";
  }
  if (
    region === "europe--central-asia" ||
    region === "europa-y-asia-central" ||
    region === "اوروبا-واسيا-الوسطى"
  ) {
    color = "yellow";
  }
  return color;
};

const TinyTile = (props) => (
  <ThemeProvider theme={invertTheme}>
    <Action
      className={props.className}
      style={props.style}
      cardColor={props.cardColor}
      to={props.to}
      aria-label={props.title}
    >
      <CornerIcon
        className="tiny-tile__corner-icon"
        cardColor={props.cardColor}
      >
        <ArrowRight size={16} strokeWidth={2} title="Internal Link" />
      </CornerIcon>
      <Wrapper
        px={4}
        py={4}
        backgroundImage={props.image}
        cardColor={props.cardColor}
      >
        <Content>
          <H4 children={props.type} mb={2} />
          <Text children={props.title} fontWeight="600" />
        </Content>
      </Wrapper>
    </Action>
  </ThemeProvider>
);

TinyTile.defaultProps = {
  title: "Region Name",
  cardColor: "green",
};

export default TinyTile;



// WEBPACK FOOTER //
// ./src/components/TinyTile.js